// src/pages/Alerts.js
import React, { useEffect, useState } from "react";
import axios from "axios";
import { FaExclamationTriangle, FaCalendar, FaFileAlt, FaUsers, FaTruck, FaShieldAlt } from "react-icons/fa";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function Alerts() {
  const [alert, setAlert] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!localStorage.getItem("auth")) window.location.href = "/login";
  }, []);

  useEffect(() => {
    async function fetchAlert() {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/alerts/latest`);
        if (res.data && res.data.alert) setAlert(res.data.data);
      } catch {
        setAlert(null);
      } finally {
        setLoading(false);
      }
    }
    fetchAlert();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex items-center gap-3 text-slate-400">
          <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
          <span className="text-sm font-medium">Loading alerts…</span>
        </div>
      </div>
    );
  }

  const imageUrl = alert
    ? `${API_BASE_URL}/images/${alert.date}/${alert.file.replace("_result.txt", ".jpg")}`
    : null;

  return (
    <div className="p-6 max-w-screen-xl space-y-6">

      {/* Header */}
      <div>
        <h1 className="text-xl font-bold text-slate-900">Safety Alerts</h1>
        <p className="text-sm text-slate-500 mt-0.5">Latest safety events from AI detection</p>
      </div>

      {!alert ? (
        <div className="card flex flex-col items-center justify-center py-20 gap-4">
          <div className="w-16 h-16 rounded-2xl bg-emerald-50 border border-emerald-100 flex items-center justify-center">
            <FaShieldAlt className="text-2xl text-emerald-500" />
          </div>
          <div>
            <div className="text-base font-semibold text-slate-700 text-center">All Clear</div>
            <div className="text-sm text-slate-400 text-center mt-1">No safety alerts have been detected.</div>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Alert banner */}
          <div className="flex items-center gap-3 px-4 py-3 rounded-2xl bg-red-50 border border-red-200">
            <FaExclamationTriangle className="text-red-500 flex-shrink-0" />
            <span className="text-sm font-semibold text-red-700">Safety Alert Detected</span>
            <span className="ml-auto text-xs text-red-400 font-medium">{alert.alert_type}</span>
          </div>

          {/* Alert card */}
          <div className="card overflow-hidden">
            <div className="grid md:grid-cols-2">
              {/* Image */}
              <div className="relative bg-slate-100">
                <img
                  src={imageUrl}
                  alt="Alert frame"
                  className="w-full h-64 md:h-full object-cover"
                />
                <div className="absolute top-3 left-3">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold
                    bg-red-600 text-white shadow-lg">
                    <span className="relative flex h-2 w-2">
                      <span className="animate-pulseRing absolute inline-flex h-full w-full rounded-full bg-red-300 opacity-75" />
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-white" />
                    </span>
                    ALERT
                  </span>
                </div>
              </div>

              {/* Details */}
              <div className="p-6 space-y-5">
                <div>
                  <div className="label mb-1.5">Alert Message</div>
                  <p className="text-sm font-medium text-slate-800 leading-relaxed">{alert.message}</p>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-slate-50 rounded-xl p-3 border border-slate-100">
                    <div className="flex items-center gap-1.5 text-xs text-slate-400 mb-1">
                      <FaCalendar className="text-[10px]" /> Date
                    </div>
                    <div className="text-sm font-semibold text-slate-700">{alert.date}</div>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-3 border border-slate-100">
                    <div className="flex items-center gap-1.5 text-xs text-slate-400 mb-1">
                      <FaExclamationTriangle className="text-[10px]" /> Type
                    </div>
                    <div className="text-sm font-semibold text-slate-700">{alert.alert_type}</div>
                  </div>
                  <div className="bg-blue-50 rounded-xl p-3 border border-blue-100">
                    <div className="flex items-center gap-1.5 text-xs text-blue-400 mb-1">
                      <FaUsers className="text-[10px]" /> Persons
                    </div>
                    <div className="text-xl font-bold text-blue-700">{alert.person_count}</div>
                  </div>
                  <div className="bg-emerald-50 rounded-xl p-3 border border-emerald-100">
                    <div className="flex items-center gap-1.5 text-xs text-emerald-400 mb-1">
                      <FaTruck className="text-[10px]" /> Machinery
                    </div>
                    <div className="text-xl font-bold text-emerald-700">{alert.machinery_count}</div>
                  </div>
                </div>

                <div>
                  <div className="label mb-1.5">Source File</div>
                  <div className="flex items-center gap-2 text-xs text-slate-500 bg-slate-50 border border-slate-100 rounded-xl px-3 py-2 truncate">
                    <FaFileAlt className="flex-shrink-0 text-slate-400" />
                    <span className="truncate">{alert.file}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Alerts;
